import { useState, useEffect } from 'react';
import type FloorPlan from '../../dashboard/models/floor-plan';
import { getFloorPlansForOffice, getUnavailableWorkplaces } from '../services/floorplan.service';
import { useKeycloak } from '../../authentication/keycloak/KeycloakProvider';

interface FloorPlansState {
  readonly initialized: boolean;
  readonly error?: boolean;
  readonly floorPlans?: FloorPlan[];
}

interface FloorPlansProps {
  officeId: string | undefined;
}

export const useFloorPlans = ({ officeId }: FloorPlansProps): FloorPlansState => {
  const [state, setState] = useState<FloorPlansState>({ initialized: false });
  const keycloak = useKeycloak();

  useEffect(() => {
    const fetchFloorPlans = async (): Promise<void> => {
      if (!officeId) {
        return;
      }
      try {
        const floorPlans = await getFloorPlansForOffice(officeId, keycloak);
        setState({ initialized: true, floorPlans });
      } catch (error) {
        setState({ initialized: false, error: true });
      }
    };

    fetchFloorPlans();
  }, [officeId, keycloak]);

  return state;
};

interface UnavailableWorkplacesState {
  readonly initialized: boolean;
  readonly error?: boolean;
  readonly unavailableWorkplaceIds?: string[];
}

interface UnavailableWorkplacesProps {
  officeId: string | undefined;
  date: string;
  dayPart: number;
}

export const useUnavailableWorkplaces = ({
  officeId,
  date,
  dayPart,
}: UnavailableWorkplacesProps): UnavailableWorkplacesState => {
  const [state, setState] = useState<UnavailableWorkplacesState>({ initialized: false });
  const keycloak = useKeycloak();

  useEffect(() => {
    const fetchUnavailable = async (): Promise<void> => {
      if (!officeId || !date) {
        return;
      }
      try {
        const unavailableWorkplaceIds = await getUnavailableWorkplaces(officeId, date, dayPart, keycloak);
        setState({ initialized: true, unavailableWorkplaceIds });
      } catch (error) {
        setState({ initialized: false, error: true });
      }
    };

    fetchUnavailable();
  }, [officeId, date, dayPart, keycloak]);

  return state;
};

interface UnavailableWorkplacesForDatesState {
  readonly initialized: boolean;
  readonly error?: boolean;
  readonly unavailableWorkplaceIds?: string[];
}

interface UnavailableWorkplacesForDatesProps {
  officeId: string | undefined;
  dates: string[];
  dayPart: number;
  /** See `getUnavailableWorkplaces`: the administrator's view wants today's elapsed half-days too. */
  includeElapsedDayPart?: boolean;
}

export const useUnavailableWorkplacesForDates = ({
  officeId,
  dates,
  dayPart,
  includeElapsedDayPart = false,
}: UnavailableWorkplacesForDatesProps): UnavailableWorkplacesForDatesState => {
  const [state, setState] = useState<UnavailableWorkplacesForDatesState>({ initialized: false });
  const keycloak = useKeycloak();
  const datesKey = dates.join(',');

  useEffect(() => {
    const fetchUnavailable = async (): Promise<void> => {
      if (!officeId || dates.length === 0) {
        return;
      }
      try {
        const allUnavailableIds = new Set<string>();
        const results = await Promise.all(
          dates.map((date) => getUnavailableWorkplaces(officeId, date, dayPart, keycloak, includeElapsedDayPart)),
        );
        results.forEach((ids) => {
          ids?.forEach((id) => allUnavailableIds.add(id));
        });
        setState({ initialized: true, unavailableWorkplaceIds: Array.from(allUnavailableIds) });
      } catch (error) {
        setState({ initialized: false, error: true });
      }
    };

    fetchUnavailable();
  }, [officeId, datesKey, dayPart, keycloak, includeElapsedDayPart]);

  return state;
};
